/**
 * STE Skate-Tec Rocker Service add-on
 * - After successful ATC of eligible blade PDPs → modal (warning → profile → cart/add.js)
 * - Cart guard: remove rocker line(s) if no eligible blade remains
 * Soft Horizon-style dialog (matches boot OOS substitute).
 */
(function () {
  const DIALOG_ID = 'ste-rocker-service-dialog';
  const CONFIG_ID = 'ste-rocker-service-config';
  const SESSION_SKIP_KEY = 'ste_rocker_skip_until';

  /** Fallback IDs if Liquid config missing (cart guard still works sitewide). */
  const DEFAULTS = {
    productId: 11150657487186,
    productHandle: 'skate-tec-rocker-service',
    price: '40.00',
    currency: 'EUR',
    eligibleHandles: [
      'skate-tec-attaq',
      'skate-tec-m-wave',
      'skate-tec-ot2',
      'skate-tec-krypton',
    ],
    variants: [
      { id: 54743771316562, profile: '5M / 10M / 3M' },
      { id: 54743773282642, profile: '6M / 11M / 5M' },
    ],
  };

  let config = null;
  let lastFocus = null;
  let interceptInstalled = false;
  let suppressingGuard = false;
  /** After a successful rocker add, skip orphan removal briefly so Horizon sync cannot yank the line. */
  let rockerProtectUntil = 0;
  /** Native fetch captured before we wrap window.fetch. */
  const nativeFetch = window.fetch.bind(window);

  function mergeConfig() {
    let fromLiquid = null;
    const el = document.getElementById(CONFIG_ID);
    if (el && el.textContent) {
      try {
        fromLiquid = JSON.parse(el.textContent);
      } catch (e) {
        console.warn('[STE rocker] bad config', e);
      }
    }
    config = Object.assign({}, DEFAULTS, fromLiquid || {});
    // Layout product object can be blank; detect eligible PDP from URL handle.
    try {
      const path = (location.pathname || '').replace(/\/+$/, '');
      const handle = decodeURIComponent(path.split('/').pop() || '');
      const list = config.eligibleHandles || DEFAULTS.eligibleHandles;
      if (list.indexOf(handle) !== -1) config.pdpEligible = true;
    } catch (e) {}
    const locale = String(config.locale || 'en').toLowerCase();
    const pack = (config.i18n && (config.i18n[locale] || config.i18n[locale.slice(0, 2)])) || null;
    if (pack && typeof pack === 'object' && pack.warning_title) {
      config.i18n = pack;
    } else if (!config.i18n || !config.i18n.warning_title) {
      config.i18n = defaultI18n(locale);
    }
    // Ensure variant IDs stay as safe numeric strings (never lose precision).
    if (Array.isArray(config.variants)) {
      config.variants = config.variants.map(function (v) {
        return {
          id: String(v.id).replace(/\D/g, ''),
          profile: v.profile,
        };
      });
    }
    return config;
  }

  function defaultI18n(locale) {
    const nl = String(locale || '').toLowerCase().startsWith('nl');
    if (nl) {
      return {
        warning_title: 'Fabrieksmatig voorgekromd — niet gerockered',
        warning_body:
          'Deze blades worden voorgekromd geleverd maar zijn niet gerockered, dus niet klaar voor het ijs. Rocker-service nu toevoegen (€40)?',
        cta_add: 'Rocker-service toevoegen (€40)',
        cta_skip: 'Doorgaan zonder rocker',
        profile_title: 'Kies rocker-profiel',
        profile_body: 'Beide profielen kosten €40. Kies het profiel dat bij je setup past.',
        profile_price: '€40',
        cta_confirm: 'Toevoegen aan winkelwagen',
        cta_back: 'Terug',
        close: 'Sluiten',
        add_error: 'Kon rocker-service niet toevoegen. Probeer het opnieuw.',
        notice_title: 'Fabrieksmatig voorgekromd — niet gerockered',
        notice_body:
          'Deze blades worden voorgekromd geleverd maar zijn niet gerockered, dus niet klaar voor het ijs. Voeg nu de STE rockerservice toe.',
      };
    }
    return {
      warning_title: 'Factory pre-bent — not rockered',
      warning_body:
        'These blades ship factory pre-bent but are not rockered, so they are not skate-ready. Add rocker service now (€40)?',
      cta_add: 'Add rocker service (€40)',
      cta_skip: 'Continue without rocker',
      profile_title: 'Choose rocker profile',
      profile_body: 'Both profiles are €40. Pick the profile that fits your setup.',
      profile_price: '€40',
      cta_confirm: 'Add to cart',
      cta_back: 'Back',
      close: 'Close',
      add_error: 'Could not add rocker service. Please try again.',
      notice_title: 'Factory pre-bent — not rockered',
      notice_body:
        'These blades ship factory pre-bent but are not rockered, so they are not skate-ready. Add the STE rockering service now.',
    };
  }

  function t(key) {
    const i18n = (config && config.i18n) || defaultI18n('en');
    return i18n[key] || key;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function isEligibleHandle(handle) {
    const list = (config && config.eligibleHandles) || DEFAULTS.eligibleHandles;
    return list.indexOf(String(handle || '')) !== -1;
  }

  function isRockerVariantId(id) {
    const vid = String(id || '').replace(/\D/g, '');
    const variants = (config && config.variants) || DEFAULTS.variants;
    return variants.some(function (v) {
      return String(v.id).replace(/\D/g, '') === vid;
    });
  }

  function isRockerLine(item) {
    if (!item) return false;
    if (item.handle === ((config && config.productHandle) || DEFAULTS.productHandle)) return true;
    if (String(item.product_id) === String((config && config.productId) || DEFAULTS.productId)) return true;
    return isRockerVariantId(item.id || item.variant_id);
  }

  function isEligibleBladeLine(item) {
    if (!item) return false;
    if (isRockerLine(item)) return false;
    if (isEligibleHandle(item.handle)) return true;
    // Fallback: product URL path sometimes present when handle missing
    try {
      const url = String(item.url || '');
      return (config.eligibleHandles || DEFAULTS.eligibleHandles).some(function (h) {
        return url.indexOf('/products/' + h) !== -1;
      });
    } catch (e) {
      return false;
    }
  }

  function cartHasEligibleBlade(items) {
    return (items || []).some(isEligibleBladeLine);
  }

  function rockerLines(items) {
    return (items || []).filter(isRockerLine);
  }

  function cartAddUrl() {
    try {
      if (window.Theme && Theme.routes && Theme.routes.cart_add_url) {
        return Theme.routes.cart_add_url;
      }
    } catch (e) {}
    return (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart/add.js';
  }

  function cartJsUrl() {
    try {
      if (window.Theme && Theme.routes && Theme.routes.cart_url) {
        return Theme.routes.cart_url + '.js';
      }
    } catch (e) {}
    return (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart.js';
  }

  function collectCartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      const id = el.getAttribute('data-section-id') || el.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    if (ids.indexOf('cart-drawer-section') === -1) ids.push('cart-drawer-section');
    return ids;
  }

  // Theme asset pipeline breaks dynamic ESM loads of @shopify/events — morph + plain event instead.
  const CART_LINES_UPDATE = 'shopify:cart:lines-update';

  function applyCartDrawerSectionHtml(html) {
    if (!html) return false;
    const existing = document.getElementById('shopify-section-cart-drawer-section');
    if (!existing) return false;
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const neu = doc.getElementById('shopify-section-cart-drawer-section');
      if (!neu) return false;
      const oldInner = existing.querySelector('[data-hydration-key="cart-drawer-inner"]');
      const newInner = neu.querySelector('[data-hydration-key="cart-drawer-inner"]');
      if (oldInner && newInner) {
        oldInner.replaceWith(document.importNode(newInner, true));
      } else {
        existing.innerHTML = neu.innerHTML;
      }
      return true;
    } catch (e) {
      console.warn('[STE rocker] drawer DOM morph failed', e);
      return false;
    }
  }

  async function fetchFreshCartSections(sectionIds) {
    let ids = (sectionIds && sectionIds.length) ? sectionIds.slice() : collectCartSectionIds();
    if (ids.indexOf('cart-drawer-section') === -1) ids.push('cart-drawer-section');
    ids = ids.filter(function (id) {
      const s = String(id || '');
      return s === 'cart-drawer-section' || s.indexOf('cart') !== -1;
    });
    if (!ids.length) ids = ['cart-drawer-section'];
    const root = window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
    const url = root + '?sections=' + ids.map(encodeURIComponent).join(',') + '&ste_ts=' + Date.now();
    try {
      const res = await nativeFetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      return await res.json().catch(function () { return null; });
    } catch (e) {
      return null;
    }
  }

  function ensureDialog() {
    let dialog = document.getElementById(DIALOG_ID);
    if (dialog) return dialog;
    dialog = document.createElement('div');
    dialog.id = DIALOG_ID;
    dialog.className = 'ste-rocker-dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-hidden', 'true');
    dialog.innerHTML =
      '<div class="ste-rocker-dialog__backdrop" data-ste-rocker-close tabindex="-1"></div>' +
      '<div class="ste-rocker-dialog__card" role="document">' +
      '  <div class="ste-rocker-dialog__header">' +
      '    <h2 class="ste-rocker-dialog__heading" id="ste-rocker-dialog-title"></h2>' +
      '    <button type="button" class="ste-rocker-dialog__close" data-ste-rocker-close aria-label="' +
      escapeHtml(t('close')) +
      '">&times;</button>' +
      '  </div>' +
      '  <div class="ste-rocker-dialog__body" data-ste-rocker-body></div>' +
      '</div>';
    document.body.appendChild(dialog);
    dialog.addEventListener('click', function (e) {
      const target = e.target;
      if (target && target.closest && target.closest('[data-ste-rocker-close]')) {
        closeModal();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && dialog.classList.contains('is-open')) {
        closeModal();
      }
    });
    return dialog;
  }

  function closeModal() {
    const dialog = document.getElementById(DIALOG_ID);
    if (!dialog) return;
    dialog.classList.remove('is-open');
    dialog.setAttribute('aria-hidden', 'true');
    document.documentElement.style.removeProperty('overflow');
    if (lastFocus && typeof lastFocus.focus === 'function') {
      try {
        lastFocus.focus();
      } catch (e) {}
    }
  }

  function openModal(step) {
    mergeConfig();
    const dialog = ensureDialog();
    const titleEl = dialog.querySelector('#ste-rocker-dialog-title');
    const body = dialog.querySelector('[data-ste-rocker-body]');
    lastFocus = document.activeElement;

    if (step === 'profiles') {
      titleEl.textContent = t('profile_title');
      const variants = config.variants || DEFAULTS.variants;
      let profilesHtml = '<div class="ste-rocker-dialog__profiles">';
      variants.forEach(function (v, idx) {
        profilesHtml +=
          '<button type="button" class="ste-rocker-dialog__profile' +
          (idx === 0 ? ' is-selected' : '') +
          '" data-ste-rocker-profile="' +
          escapeHtml(String(v.id).replace(/\D/g, '')) +
          '">' +
          '<span class="ste-rocker-dialog__profile-name">' +
          escapeHtml(v.profile) +
          '</span>' +
          '<span class="ste-rocker-dialog__profile-meta">' +
          escapeHtml(t('profile_price')) +
          '</span>' +
          '</button>';
      });
      profilesHtml += '</div>';
      body.innerHTML =
        '<p class="ste-rocker-dialog__msg">' +
        escapeHtml(t('profile_body')) +
        '</p>' +
        '<div class="ste-rocker-dialog__error" data-ste-rocker-error hidden></div>' +
        profilesHtml +
        '<div class="ste-rocker-dialog__actions">' +
        '<button type="button" class="ste-rocker-dialog__btn" data-ste-rocker-confirm>' +
        escapeHtml(t('cta_confirm')) +
        '</button>' +
        '<button type="button" class="ste-rocker-dialog__btn ste-rocker-dialog__btn--secondary" data-ste-rocker-back>' +
        escapeHtml(t('cta_back')) +
        '</button>' +
        '</div>';

      let selectedId = String(variants[0].id).replace(/\D/g, '');
      body.querySelectorAll('[data-ste-rocker-profile]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          body.querySelectorAll('[data-ste-rocker-profile]').forEach(function (b) {
            b.classList.remove('is-selected');
          });
          btn.classList.add('is-selected');
          selectedId = btn.getAttribute('data-ste-rocker-profile');
        });
      });
      body.querySelector('[data-ste-rocker-back]').addEventListener('click', function () {
        openModal('warning');
      });
      body.querySelector('[data-ste-rocker-confirm]').addEventListener('click', function () {
        addRockerVariant(selectedId, body);
      });
    } else {
      titleEl.textContent = t('warning_title');
      body.innerHTML =
        '<p class="ste-rocker-dialog__msg">' +
        escapeHtml(t('warning_body')) +
        '</p>' +
        '<div class="ste-rocker-dialog__actions">' +
        '<button type="button" class="ste-rocker-dialog__btn" data-ste-rocker-add>' +
        escapeHtml(t('cta_add')) +
        '</button>' +
        '<button type="button" class="ste-rocker-dialog__btn ste-rocker-dialog__btn--secondary" data-ste-rocker-skip>' +
        escapeHtml(t('cta_skip')) +
        '</button>' +
        '</div>';
      body.querySelector('[data-ste-rocker-add]').addEventListener('click', function () {
        openModal('profiles');
      });
      body.querySelector('[data-ste-rocker-skip]').addEventListener('click', function () {
        try {
          sessionStorage.setItem(SESSION_SKIP_KEY, String(Date.now() + 2 * 60 * 1000));
        } catch (e) {}
        closeModal();
      });
    }

    dialog.classList.add('is-open');
    dialog.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    const focusBtn = body.querySelector('button');
    if (focusBtn) focusBtn.focus();
  }

  function shouldSkipPrompt() {
    try {
      const until = Number(sessionStorage.getItem(SESSION_SKIP_KEY) || 0);
      if (until && Date.now() < until) return true;
    } catch (e) {}
    return false;
  }

  async function fetchCart() {
    const res = await nativeFetch(cartJsUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('cart.js ' + res.status);
    return res.json();
  }

  function cartHasVariant(cart, variantId) {
    const want = String(variantId).replace(/\D/g, '');
    return (cart.items || []).some(function (item) {
      return String(item.variant_id || item.id).replace(/\D/g, '') === want;
    });
  }

  async function postCartAdd(variantId, sectionIds) {
    const idNum = Number(String(variantId).replace(/\D/g, ''));
    if (!idNum || !isFinite(idNum)) {
      throw new Error(t('add_error') + ' (bad variant id)');
    }
    const payload = {
      items: [{ id: idNum, quantity: 1 }],
    };
    if (sectionIds && sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }
    const res = await nativeFetch(cartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(function () {
      return {};
    });
    if (!res.ok || data.status || data.errors) {
      throw new Error(
        data.description || data.message || (typeof data.errors === 'string' ? data.errors : null) || t('add_error')
      );
    }
    return data;
  }

  async function dispatchHorizonCartAdd(variantId, addResponse, cart) {
    let sections = (addResponse && addResponse.sections) || null;
    if (!sections || !sections['cart-drawer-section']) {
      const fresh = await fetchFreshCartSections(collectCartSectionIds());
      if (fresh) sections = fresh;
    }
    if (sections && sections['cart-drawer-section']) {
      applyCartDrawerSectionHtml(sections['cart-drawer-section']);
    }

    let resolveFn, rejectFn;
    const promise = new Promise(function (resolve, reject) {
      resolveFn = resolve;
      rejectFn = reject;
    });

    let evt;
    try {
      evt = new Event(CART_LINES_UPDATE, { bubbles: true, cancelable: true });
    } catch (e) {
      evt = document.createEvent('Event');
      evt.initEvent(CART_LINES_UPDATE, true, true);
    }
    evt.action = 'add';
    evt.context = 'product';
    evt.lines = [{
      merchandiseId: 'gid://shopify/ProductVariant/' + String(variantId).replace(/\D/g, ''),
      quantity: 1,
    }];
    evt.promise = promise;

    try {
      document.dispatchEvent(evt);
    } catch (e) {
      console.warn('[STE rocker] cart event dispatch failed', e);
    }

    const cartPayload = cart && cart.token != null
      ? {
          id: 'gid://shopify/Cart/' + String(cart.token).split('?')[0],
          totalQuantity: cart.item_count || 0,
        }
      : cart
        ? { id: null, totalQuantity: cart.item_count || 0 }
        : null;

    try {
      resolveFn({
        cart: cartPayload,
        detail: {
          items: (cart && cart.items) || [],
          itemCount: (cart && cart.item_count) || 0,
          source: 'ste-rocker',
          sections: sections || null,
          didError: false,
        },
      });
    } catch (e) {
      try { rejectFn(e); } catch (e2) {}
    }

    try {
      const icon = document.querySelector('cart-icon');
      if (icon && typeof icon.renderCartBubble === 'function') {
        icon.renderCartBubble((cart && cart.item_count) || 0, true);
      }
    } catch (e) {}
  }

  async function openCartUi() {
    try {
      if (window.Shopify && Shopify.actions && typeof Shopify.actions.openCart === 'function') {
        await Shopify.actions.openCart();
        return;
      }
    } catch (e) {}
    const drawer = document.querySelector('cart-drawer-component, cart-drawer, theme-drawer[id*="cart" i]');
    if (drawer && typeof drawer.open === 'function') {
      try {
        drawer.open();
        return;
      } catch (e) {}
    }
    const themeDrawer = document.querySelector('theme-drawer');
    if (themeDrawer && typeof themeDrawer.open === 'function') {
      try {
        themeDrawer.open();
      } catch (e) {}
    }
  }

  async function addRockerVariant(variantId, bodyEl) {
    const errEl = bodyEl && bodyEl.querySelector('[data-ste-rocker-error]');
    const confirmBtn = bodyEl && bodyEl.querySelector('[data-ste-rocker-confirm]');
    if (errEl) {
      errEl.hidden = true;
      errEl.textContent = '';
    }
    if (confirmBtn) confirmBtn.disabled = true;
    suppressingGuard = true;
    rockerProtectUntil = Date.now() + 8000;
    try {
      mergeConfig();
      const sectionIds = collectCartSectionIds();
      const addResponse = await postCartAdd(variantId, sectionIds);

      // Verify Ajax cart actually contains the rocker line
      let cart = await fetchCart();
      if (!cartHasVariant(cart, variantId)) {
        // Retry once with classic single-item payload (no items[])
        const idNum = Number(String(variantId).replace(/\D/g, ''));
        const res2 = await nativeFetch(cartAddUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: idNum,
            quantity: 1,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        });
        const data2 = await res2.json().catch(function () {
          return {};
        });
        if (!res2.ok || data2.status) {
          throw new Error(data2.description || data2.message || t('add_error'));
        }
        cart = await fetchCart();
        if (!cartHasVariant(cart, variantId)) {
          throw new Error(t('add_error'));
        }
        Object.assign(addResponse, data2);
      }

      closeModal();
      await dispatchHorizonCartAdd(variantId, addResponse, cart);
      try {
        await openCartUi();
      } catch (e) {}
    } catch (e) {
      console.warn('[STE rocker] add failed', e);
      if (errEl) {
        errEl.hidden = false;
        errEl.textContent = (e && e.message) || t('add_error');
      }
      rockerProtectUntil = 0;
    } finally {
      suppressingGuard = false;
      if (confirmBtn) confirmBtn.disabled = false;
    }
  }

  async function removeOrphanRockers() {
    if (suppressingGuard) return;
    if (Date.now() < rockerProtectUntil) return;
    mergeConfig();
    let cart;
    try {
      cart = await fetchCart();
    } catch (e) {
      return;
    }
    const items = cart.items || [];
    const orphans = rockerLines(items);
    if (!orphans.length) return;
    if (cartHasEligibleBlade(items)) return;

    const updates = {};
    orphans.forEach(function (line) {
      updates[String(line.key || line.id)] = 0;
    });
    try {
      suppressingGuard = true;
      var updateUrl = '/cart/update.js';
      try {
        if (window.Theme && Theme.routes && Theme.routes.cart_update_url) {
          updateUrl = Theme.routes.cart_update_url;
          if (updateUrl.indexOf('.js') === -1) updateUrl += '.js';
        }
      } catch (e) {}
      await nativeFetch(updateUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates: updates }),
        });
    } catch (e) {
      console.warn('[STE rocker] orphan remove failed', e);
    } finally {
      suppressingGuard = false;
    }
  }

  function maybePromptAfterAdd(payload) {
    // Blade add-to-cart popup removed: never offer rocker service when a blade is added.
    return;
    mergeConfig();
    // Unified blade add-ons modal owns ATTAQ + M-Wave; skip rocker-only popup there.
    if (window.__STE_BLADE_ADDONS_UNIFIED || document.documentElement.classList.contains('ste-blade-addons-pdp')) {
      return;
    }
    if (!config.pdpEligible) return;
    const items = normalizeAddedItems(payload);
    if (!items.length) return;

    const addedEligible = items.some(function (it) {
      return isEligibleHandle(it.handle) && !isRockerLine(it);
    });
    // Also match when handle missing but product_title / url points at blade
    const addedEligibleLoose =
      addedEligible ||
      items.some(function (it) {
        if (isRockerLine(it)) return false;
        const url = String(it.url || '');
        return (config.eligibleHandles || DEFAULTS.eligibleHandles).some(function (h) {
          return url.indexOf('/products/' + h) !== -1;
        });
      });
    if (!addedEligibleLoose) return;

    if (items.some(isRockerLine) && items.every(isRockerLine)) return;
    if (shouldSkipPrompt()) return;
    if (suppressingGuard) return;

    openModal('warning');
  }

  function normalizeAddedItems(payload) {
    if (!payload) return [];
    if (Array.isArray(payload.items)) return payload.items;
    if (payload.id && (payload.handle || payload.product_id || payload.product_title)) {
      return [payload];
    }
    if (payload.variant_id || payload.id) {
      return [payload];
    }
    return [];
  }

  function installFetchIntercept() {
    if (interceptInstalled) return;
    interceptInstalled = true;
    window.fetch = async function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = (
        (init && init.method) ||
        (typeof input !== 'string' && input && input.method) ||
        'GET'
      ).toUpperCase();
      const isCartAdd = method === 'POST' && /\/cart\/add(\.js)?(\?|$)/i.test(String(url));
      const isCartChange =
        method === 'POST' && /\/cart\/(change|update|clear)(\.js)?(\?|$)/i.test(String(url));

      const res = await nativeFetch(input, init);

      if (isCartAdd && !suppressingGuard) {
        try {
          const clone = res.clone();
          const data = await clone.json();
          if (!data.status) {
            setTimeout(function () {
              maybePromptAfterAdd(data);
              removeOrphanRockers();
            }, 120);
          }
        } catch (e) {}
      } else if (isCartChange && !suppressingGuard) {
        setTimeout(function () {
          removeOrphanRockers();
        }, 150);
      }
      return res;
    };
  }

  function injectPdpNotice() {
    /* Liquid snippet renders the amber notice on eligible PDPs. */
    return;
  }

  // Public inline API for the PDP "Add rockering service" button (no modal).
  window.STERockerService = {
    add: async function (variantId) {
      suppressingGuard = true;
      rockerProtectUntil = Date.now() + 8000;
      try {
        mergeConfig();
        const sectionIds = collectCartSectionIds();
        const addResponse = await postCartAdd(variantId, sectionIds);
        const cart = await fetchCart();
        if (!cartHasVariant(cart, variantId)) throw new Error(t('add_error'));
        await dispatchHorizonCartAdd(variantId, addResponse, cart);
        try { await openCartUi(); } catch (e) {}
        return cart;
      } catch (e) {
        rockerProtectUntil = 0;
        throw e;
      } finally {
        suppressingGuard = false;
      }
    },
  };

  function init() {
    mergeConfig();
    installFetchIntercept();
    injectPdpNotice();
    setTimeout(removeOrphanRockers, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
